import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Briefcase } from 'lucide-react';
import LoadingSpinner from '../shared/LoadingSpinner';
import EmptyState from '../shared/EmptyState';
import ErrorState from '../shared/ErrorState';
import { useLaunchDeals } from '../../hooks/useLaunchDeals';
import { useAppPermissions } from '../../hooks/useAppPermissions';
import LaunchDealCreateModal from './LaunchDealCreateModal';

const STAGE_COLORS = {
  novo:               'bg-slate-100 text-slate-700',
  em_qualificacao:    'bg-sky-100 text-sky-700',
  proposta_enviada:   'bg-violet-100 text-violet-700',
  em_negociacao:      'bg-amber-100 text-amber-700',
  contrato_assinado:  'bg-emerald-100 text-emerald-700',
  encerrado_ganho:    'bg-green-100 text-green-700',
  encerrado_perdido:  'bg-red-100 text-red-700',
};

function fmtBRL(v) {
  if (v == null) return '—';
  const n = Number(v);
  if (!isFinite(n)) return '—';
  return n.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}
function fmtDate(d) {
  if (!d) return '—';
  try { return new Date(d).toLocaleDateString('pt-BR'); } catch { return d; }
}
function truncate(s, n) {
  if (!s) return '—';
  if (s.length <= n) return s;
  return s.slice(0, n - 1) + '…';
}

export default function LaunchDealList() {
  const { permissions } = useAppPermissions();
  const { deals, loading, error, refetch } = useLaunchDeals();
  const [createOpen, setCreateOpen] = useState(false);
  const canWrite = permissions.includes('launches.write');

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-[11px] text-slate-500">
          {loading ? '...' : `${deals.length} deal${deals.length === 1 ? '' : 's'}`}
        </div>
        <button
          type="button"
          onClick={() => setCreateOpen(true)}
          disabled={!canWrite}
          title={canWrite ? 'Criar novo deal' : 'Sem permissão de escrita. Solicite a um admin.'}
          className="px-2.5 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded text-xs flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <Plus size={13} /> Novo deal
        </button>
      </div>

      {loading && <LoadingSpinner />}
      {!loading && error && <ErrorState message={error.message || String(error)} />}
      {!loading && !error && deals.length === 0 && (
        <EmptyState
          icon={Briefcase}
          message="Nenhum deal ainda"
          hint={canWrite ? 'Crie o primeiro deal de empreendimentos.' : 'Aguarde permissão de escrita para criar deals.'}
        />
      )}

      {!loading && !error && deals.length > 0 && (
        <>
          {/* Tabela desktop */}
          <div className="hidden md:block bg-white border border-slate-200 rounded overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
                <tr>
                  <th className="px-2 py-1.5 text-left">Empresa</th>
                  <th className="px-2 py-1.5 text-left">Título</th>
                  <th className="px-2 py-1.5 text-left">Stage</th>
                  <th className="px-2 py-1.5 text-right">Valor</th>
                  <th className="px-2 py-1.5 text-left">Próximo passo</th>
                  <th className="px-2 py-1.5 text-left">Vencimento</th>
                </tr>
              </thead>
              <tbody>
                {deals.map((d) => (
                  <tr key={d.id} className="border-t border-slate-100 hover:bg-violet-50">
                    <td className="px-2 py-1.5 text-slate-700 truncate max-w-[180px]">
                      {d.empresa?.empresa || d.empresa?.company_name || '—'}
                    </td>
                    <td className="px-2 py-1.5">
                      <Link to={`/launches/deals/${d.id}`} className="text-violet-700 hover:underline">
                        {d.title}
                      </Link>
                    </td>
                    <td className="px-2 py-1.5">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] ${STAGE_COLORS[d.stage] || 'bg-slate-100 text-slate-700'}`}>
                        {d.stage}
                      </span>
                    </td>
                    <td className="px-2 py-1.5 text-right tabular-nums text-slate-700 whitespace-nowrap">{fmtBRL(d.valor_estimado)}</td>
                    <td className="px-2 py-1.5 text-slate-600 truncate max-w-[260px]" title={d.next_step || ''}>
                      {truncate(d.next_step, 60)}
                    </td>
                    <td className="px-2 py-1.5 text-slate-600 whitespace-nowrap">{fmtDate(d.next_step_due_date)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Cards mobile */}
          <div className="md:hidden space-y-2">
            {deals.map((d) => (
              <Link
                key={d.id}
                to={`/launches/deals/${d.id}`}
                className="block bg-white border border-slate-200 rounded p-2 text-xs hover:bg-violet-50"
              >
                <div className="flex items-center justify-between gap-2 mb-1">
                  <div className="font-semibold text-slate-800 truncate flex-1">{d.title}</div>
                  <span className={`px-1.5 py-0.5 rounded text-[10px] ${STAGE_COLORS[d.stage] || 'bg-slate-100 text-slate-700'}`}>
                    {d.stage}
                  </span>
                </div>
                <div className="text-[11px] text-slate-600 truncate">
                  {d.empresa?.empresa || d.empresa?.company_name || '—'}
                </div>
                <div className="flex items-center justify-between text-[11px] text-slate-500 mt-1">
                  <span>{fmtBRL(d.valor_estimado)}</span>
                  <span>{fmtDate(d.next_step_due_date)}</span>
                </div>
                {d.next_step && (
                  <div className="text-[11px] text-slate-500 truncate mt-0.5">{truncate(d.next_step, 60)}</div>
                )}
              </Link>
            ))}
          </div>
        </>
      )}

      <LaunchDealCreateModal
        isOpen={createOpen}
        onClose={() => setCreateOpen(false)}
        onCreated={() => refetch()}
      />
    </div>
  );
}
